if (!customElements.get('results-view')) {
  class ResultsView extends HTMLElement {
    constructor() {
      super()
      this.sectionId = this.dataset.sectionId
      this.debouncedOnChange = debounce(this.onChange.bind(this), 300)
      this.mediaQuery = window.matchMedia('(min-width: 750px)')
    }

    connectedCallback() {
      this.addEventListener('input', this.debouncedOnChange)
      this.addEventListener('change', this.debouncedOnChange)
      this.addEventListener('click', this.onClick.bind(this))
      this.mediaQuery.addEventListener('change', this.onMediaQueryChange.bind(this))
    }

    onChange(e) {
      const target = e.target

      if (target.tagName === 'A') return

      const priceRange = target.closest('price-range')
      if (priceRange && e.type !== 'change') return

      const isFacet = target.closest('.sidebar-filters, .drawer-filters, .toolbar__sort')
      if (!isFacet) return

      const searchParameters = this.getSearchParameters(target)
      this.updateResults(searchParameters)
    }

    getSearchParameters(target) {
      const currentForm = target.closest('form')
      let parameters

      if (currentForm) {
        parameters = new URLSearchParams(new FormData(currentForm))
      } else {
        parameters = new URLSearchParams(window.location.search)
      }

      const toolbarSort = this.querySelector('.toolbar__sort select')
      if (toolbarSort) {
        if (target === toolbarSort) {
          parameters.set('sort_by', toolbarSort.value)
        } else if (!parameters.has('sort_by')) {
          parameters.set('sort_by', toolbarSort.value)
        }
      }

      return parameters.toString()
    }

    updateResults(searchParameters) {
      const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParameters}`
      const activeElementId = document.activeElement?.id

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const targets = this.querySelectorAll('[data-render-target]')

          this.preserveShowMoreState(html)

          targets.forEach((target) => {
            const targetName = target.getAttribute('data-render-target')
            const source = html.querySelector(`[data-render-target="${targetName}"]`)
            if (target && source) target.innerHTML = source.innerHTML
          })

          this.syncSortValues(searchParameters)

          if (activeElementId) {
            document.getElementById(activeElementId)?.focus()
          }
        })

      this.updateURL(searchParameters)
    }

    preserveShowMoreState(html) {
      const expandedButtons = this.querySelectorAll('show-more-filters button[aria-expanded="true"]')

      expandedButtons.forEach((button) => {
        const showMore = button.closest('show-more-filters')
        if (!showMore || !showMore.id) return

        const id = showMore.id
        const incomingShowMore = html.querySelector(`show-more-filters#${id}`)

        if (incomingShowMore) {
          const incomingButton = incomingShowMore.querySelector('button')
          if (incomingButton) {
            incomingButton.setAttribute('aria-expanded', 'true')
          }

          const incomingFilterValues = incomingShowMore.closest('[data-filter-values]')
          if (incomingFilterValues) {
            const hiddenItems = incomingFilterValues.querySelectorAll('.show-more-item')
            hiddenItems.forEach((item) => item.classList.remove('hidden'))
          }
        }
      })
    }

    syncSortValues(searchParameters) {
      const parameters = new URLSearchParams(searchParameters)
      const sortBy = parameters.get('sort_by')
      if (!sortBy) return

      const toolbarSort = this.querySelector('.toolbar__sort select')
      if (toolbarSort) toolbarSort.value = sortBy

      const drawerSort = this.querySelectorAll('input[name="sort_by"]')
      drawerSort.forEach((radio) => {
        radio.checked = radio.value === sortBy
      })
    }

    onClick(e) {
      const activeFilterLink = e.target.closest('.active-filter')
      if (!activeFilterLink) return

      e.preventDefault()
      const searchParameters = new URL(activeFilterLink.href).searchParams.toString()
      this.updateResults(searchParameters)
    }

    onMediaQueryChange(e) {
      if (e.matches) {
        const drawerFiltersDialog = this.querySelector('.drawer-filters__dialog')
        if (drawerFiltersDialog && drawerFiltersDialog.hasAttribute('open')) {
          drawerFiltersDialog.closeDialog()
        }
      }
    }

    updateURL(searchParameters) {
      history.pushState(
        { searchParameters },
        '',
        `${window.location.pathname}${searchParameters && '?'.concat(searchParameters)}`
      )
    }
  }

  customElements.define('results-view', ResultsView)
}

if (!customElements.get('show-more-filters')) {
  class ShowMoreFilters extends HTMLElement {
    constructor() {
      super()
      this.button = this.querySelector('button')
      this.filterValues = this.closest('[data-filter-values]')
      this.hiddenItems = this.filterValues?.querySelectorAll('.show-more-item')

      if (this.button && this.hiddenItems.length > 0) {
        this.button.addEventListener('click', this.onButtonClick.bind(this))
      }
    }

    onButtonClick() {
      const isExpanded = this.button.getAttribute('aria-expanded') === 'true'
      this.toggleState(!isExpanded)
    }

    toggleState(isExpanded) {
      if (!this.button || !this.hiddenItems) return

      this.button.setAttribute('aria-expanded', isExpanded.toString())
      this.hiddenItems.forEach((item) => {
        item.classList.toggle('hidden', !isExpanded)
      })
    }
  }

  customElements.define('show-more-filters', ShowMoreFilters)
}

if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super()
      this.minInput = this.querySelector('.price-range__input--from')
      this.maxInput = this.querySelector('.price-range__input--to')

      this.absoluteMax = this.maxInput.getAttribute('max')
      this.absoluteMin = this.minInput.getAttribute('min') || 0

      this.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', this.onRangeChange.bind(this))
      })

      this.setMinAndMaxValues()
    }

    onRangeChange(e) {
      this.adjustToValidValues(e.currentTarget)
      this.setMinAndMaxValues()
    }

    setMinAndMaxValues() {
      if (this.maxInput.value) {
        this.minInput.setAttribute('max', this.maxInput.value)
      } else {
        this.minInput.setAttribute('max', this.absoluteMax)
      }

      if (this.minInput.value) {
        this.maxInput.setAttribute('min', this.minInput.value)
      } else {
        this.maxInput.setAttribute('min', this.absoluteMin)
      }
    }

    adjustToValidValues(input) {
      const value = Number(input.value)
      const min = Number(input.getAttribute('min'))
      const max = Number(input.getAttribute('max'))

      if (input.value === '') return

      if (value < min) {
        input.value = min
      }

      if (value > max) {
        input.value = max
      }
    }
  }

  customElements.define('price-range', PriceRange)
}
